import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { MoreVertical, X } from 'lucide-react';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu whenever the route changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  // Prevent background scroll while the mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const navItems = [
    { label: 'About', to: '/', hash: 'about', match: '/' },
    { label: 'Tuition', to: '/tuition', match: '/tuition' },
    { label: 'Resources', to: '/resources', match: '/resources' },
    { label: 'Contact', to: '/tuition', hash: 'contact' },
  ];

  const isActive = (item) => {
    if (!item.match) return false;
    if (location.pathname === item.match) return true;
    // treat legacy /programs as /tuition
    if (item.match === '/tuition' && location.pathname === '/programs') return true;
    return false;
  };

  const go = (item) => {
    setMenuOpen(false);
    if (item.to === location.pathname) {
      if (item.hash) {
        const el = document.getElementById(item.hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      navigate(item.hash ? `${item.to}#${item.hash}` : item.to);
    }
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled || menuOpen ? 'bg-white/95 backdrop-blur-md border-b border-neutral-100' : 'bg-white/70 backdrop-blur-sm'}`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="font-display text-[15px] md:text-[17px] tracking-tight font-medium text-neutral-900">
          Bio & Chem with Hamza
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-10">
          {navItems.map((item) => {
            const active = isActive(item);
            return (
              <button
                key={item.label}
                onClick={() => go(item)}
                className="relative text-[15px] text-neutral-800 hover:text-neutral-500 transition-colors duration-200 pb-1"
              >
                {item.label}
                {active && (
                  <motion.span
                    layoutId="nav-underline-desktop"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    style={{ originX: 0 }}
                    className="absolute left-0 right-0 -bottom-0.5 h-[2px] bg-neutral-900 rounded-full"
                  />
                )}
              </button>
            );
          })}
        </nav>

        {/* Right side: Book now (desktop) + 3-dot menu (mobile) */}
        <div className="flex items-center gap-2">
          <a
            href="https://forms.gle/3aGFXDkKs1pNn4wU7"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-block pill-btn bg-neutral-900 text-white px-6 py-2.5 text-[14px] font-medium hover:bg-neutral-700"
          >
            Book now
          </a>

          <button
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            className="md:hidden w-10 h-10 -mr-2 flex items-center justify-center rounded-full hover:bg-neutral-100 transition-colors"
          >
            {menuOpen ? <X size={22} /> : <MoreVertical size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden border-t border-neutral-100 bg-white/95 backdrop-blur-md"
          >
            <nav className="px-6 py-3 flex flex-col">
              {navItems.map((item) => {
                const active = isActive(item);
                return (
                  <button
                    key={item.label}
                    onClick={() => go(item)}
                    className="relative text-left text-[16px] py-3 text-neutral-800 hover:text-neutral-500 border-b border-neutral-100 last:border-b-0 transition-colors"
                  >
                    <span className="relative inline-block pb-0.5">
                      {item.label}
                      {active && (
                        <motion.span
                          initial={{ scaleX: 0 }}
                          animate={{ scaleX: 1 }}
                          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                          style={{ originX: 0 }}
                          className="absolute left-0 right-0 -bottom-0.5 h-[2px] bg-neutral-900 rounded-full"
                        />
                      )}
                    </span>
                  </button>
                );
              })}
              <a
                href="https://forms.gle/3aGFXDkKs1pNn4wU7"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMenuOpen(false)}
                className="pill-btn mt-4 mb-2 bg-neutral-900 text-white py-3 text-[14px] font-medium hover:bg-neutral-700 text-center"
              >
                Book now
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
